/**
 * HomeLandingPage.js — Landing informativa estilo Send Money (Feb 2026).
 *
 * Se monta como la "página de inicio" para Gestor, Provider CU y CEO. Toma las
 * tasas vivas de `usdt_config` y arma una "Tabla de Conversiones" con el
 * costo y la venta por moneda destino, igual al diseño que aprobó el CEO.
 *
 * Privacidad interna: el gestor NUNCA ve la palabra "USDT" — para él se
 * llama "USD" en la UI (alias enmascarado). Para CEO/Provider sí dice USDT.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, ShieldCheck, Zap, Globe, Clock, ArrowRight, CreditCard, User, Phone, Inbox, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ConversionExamplesTable from '@/components/landing/ConversionExamplesTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomeLandingPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [pause, setPause] = useState(null);
  const [pendingOps, setPendingOps] = useState([]);
  const isGestor = user?.role === 'gestor';
  const isProvider = user?.role === 'payment_agent';

  useEffect(() => {
    (async () => {
      try {
        const r2 = await axios.get(`${API}/service-pause`, { headers: getAuthHeaders() });
        setPause(r2.data);
      } catch (_) { /* silent */ }
      // Proveedor CU: fetch his assigned-but-not-yet-delivered remittances
      // so he sees on the very landing what he has to pay today.
      if (isProvider) {
        try {
          const ops = await axios.get(`${API}/operations?type=remittance&limit=20`, { headers: getAuthHeaders() });
          const active = (ops.data?.operations || []).filter(
            (o) => !['DELIVERED', 'REJECTED', 'CANCELED', 'FAILED'].includes(o.state),
          );
          setPendingOps(active);
        } catch (_) { /* silent */ }
      }
    })();
  }, [getAuthHeaders, isProvider]);

  // Compose the conversion table. Each row shows: 1 UYU → costo & venta USDT,
  // luego 1 USDT → moneda destino (compra/venta).
  // (Iter 64) Tabla pública con ejemplos se compone en `<ConversionExamplesTable>`.

  const goSendPath = isGestor
    ? '/remesas/enviar'
    : (user?.role === 'admin' ? '/admin/remesas/enviar' : '/provider');

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="home-landing-page">
      {/* ──────── HERO ──────── */}
      <Card className="p-8 border-0 shadow-sm bg-gradient-to-br from-emerald-600 via-teal-700 to-emerald-700 text-white relative overflow-hidden">
        <div className="absolute -right-12 -top-12 opacity-10">
          <Globe className="w-72 h-72" strokeWidth={1} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-medium mb-3">
            <Send className="w-3.5 h-3.5" />
            Aircom Connect · Send Money
          </div>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
            Envía dinero con tasas claras<br />
            <span className="text-emerald-200">y entregas en menos de 24h.</span>
          </h1>
          <p className="text-emerald-50 text-sm md:text-base max-w-2xl mt-3">
            Conectamos Uruguay con Cuba para que tus seres queridos reciban lo que necesitan,
            cuando lo necesitan. Tasas vivas, soporte 24/7 y comprobantes instantáneos.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to={goSendPath}>
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid="hero-send-btn">
                <Send className="w-4 h-4 mr-2" /> Enviar remesa ahora
              </Button>
            </Link>
            {pause?.is_paused_global && (
              <span className="inline-flex items-center gap-2 bg-amber-500/30 backdrop-blur border border-amber-300/50 px-3 py-2 rounded-md text-xs text-amber-50">
                ⏸️ Servicio pausado · {pause.note || 'volverá pronto'}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* ──────── PROVEEDOR CU · Remesas asignadas ──────── */}
      {isProvider && pendingOps.length > 0 && (
        <ProviderPendingOps ops={pendingOps} />
      )}

      {/* ──────── TABLA DE CONVERSIONES (publicable, con ejemplos) ──────── */}
      <ConversionExamplesTable getAuthHeaders={getAuthHeaders} sendPath={goSendPath} />

      {/* ──────── ¿Por qué elegir? ──────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Zap,         t: 'Entregas rápidas',        d: 'Menos de 24h para Cuba. Soporte directo en WhatsApp con el destinatario.' },
          { icon: ShieldCheck, t: '100% transparente',       d: 'Tasas claras, sin comisiones ocultas. Cada paso queda registrado y visible.' },
          { icon: Clock,       t: 'Comprobante al instante', d: 'Recibís un PDF al instante y podés compartirlo por WhatsApp con un solo clic.' },
        ].map((f) => (
          <Card key={f.t} className="p-5 border-0 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
              <f.icon className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-slate-900">{f.t}</h3>
            <p className="text-sm text-slate-600 mt-1">{f.d}</p>
          </Card>
        ))}
      </div>

      {/* ──────── 4 pasos ──────── */}
      <Card className="p-6 border-0 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900 mb-1">Envía dinero en 4 pasos</h2>
        <p className="text-sm text-slate-500 mb-5">Diseñado para ser tan simple como mandar un WhatsApp.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: 1, t: 'Elegí monto y moneda', d: 'UYU como origen → CUP, MLC o USD como destino.' },
            { n: 2, t: 'Cargá la tarjeta',     d: 'Sistema valida automáticamente que el BIN corresponda al banco correcto.' },
            { n: 3, t: 'Confirmá y pagá',      d: 'Con tu saldo en wallet o transferencia directa.' },
            { n: 4, t: 'Listo, recibe en 24h', d: 'Vos y el destinatario reciben confirmación al instante.' },
          ].map((s, i) => (
            <div key={s.n} className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center font-bold mb-2 shadow-md">
                {s.n}
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">{s.t}</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.d}</p>
              {i < 3 && (
                <ArrowRight className="hidden md:block absolute -right-2 top-3 w-4 h-4 text-emerald-200" />
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to={goSendPath}>
            <Button className="bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="footer-send-btn">
              Empezar a enviar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
};

// ── Proveedor CU widget: muestra remesas asignadas listas para pagar.
// Tarjeta compacta con monto, tarjeta + copiar, beneficiario y CTA.
const formatCard = (raw) => String(raw || '').replace(/\D+/g, '').replace(/(.{4})(?=.)/g, '$1 ');

const QuickCopy = ({ value, label, testId }) => {
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value || ''));
      toast.success(`${label}: copiado`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };
  return (
    <button onClick={copy} data-testid={testId}
      className="inline-flex items-center justify-center w-6 h-6 rounded border border-slate-300 hover:bg-slate-100 text-slate-500 ml-1"
      title={`Copiar ${label}`}>
      <Copy className="w-3 h-3" />
    </button>
  );
};

const ProviderPendingOps = ({ ops }) => (
  <Card className="p-6 border-2 border-emerald-200 shadow-sm bg-gradient-to-br from-emerald-50/50 to-white" data-testid="provider-pending-card">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Inbox className="w-5 h-5 text-emerald-600" />
          Remesas para pagar
          <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold" data-testid="provider-pending-count">
            {ops.length}
          </span>
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Asignadas a ti — clic en una para ver todos los datos y marcar como entregada.
        </p>
      </div>
      <Link to="/provider/operaciones">
        <Button variant="outline" size="sm" data-testid="provider-see-all">
          Ver todas <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </Link>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {ops.slice(0, 6).map((o) => {
        const md = o.metadata || {};
        const card = String(md.card_number || '').replace(/\D+/g, '');
        const phone = String(md.beneficiary_phone || md.client_phone || '').replace(/\D+/g, '');
        const payout = o.client_payout_amount ?? md.amount_received;
        const opShort = (o.id || '').slice(0, 8).toUpperCase();
        const senderName = o.created_by_info?.name || o.gestor_info?.name;
        return (
          <Link to={`/provider/operations/${o.id}`} key={o.id} className="block"
                data-testid={`provider-pending-${o.id}`}>
            <div className="rounded-lg border bg-white p-3 hover:shadow-md hover:border-emerald-300 transition">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-mono font-bold text-slate-500 uppercase">#{opShort}</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">
                  {o.state}
                </span>
              </div>
              {payout != null && (
                <div className="mb-2">
                  <p className="text-[10px] uppercase text-slate-500">Pagar al cliente</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">
                    {Number(payout).toLocaleString('es-ES', { minimumFractionDigits: 2 })} {o.currency_to}
                  </p>
                </div>
              )}
              {card && (
                <div className="text-xs flex items-center mb-1">
                  <CreditCard className="w-3 h-3 text-slate-400 mr-1" />
                  <span className="font-mono tracking-wider text-slate-900">{formatCard(card)}</span>
                  <QuickCopy value={card} label="Tarjeta" testId={`copy-card-${o.id}`} />
                </div>
              )}
              {md.beneficiary && (
                <div className="text-xs flex items-center text-slate-700 mb-1">
                  <User className="w-3 h-3 text-slate-400 mr-1" />{md.beneficiary}
                </div>
              )}
              {phone && (
                <div className="text-xs flex items-center text-slate-700">
                  <Phone className="w-3 h-3 text-slate-400 mr-1" />
                  <a href={`https://wa.me/${phone}`} target="_blank" rel="noopener noreferrer"
                     onClick={(e) => e.stopPropagation()}
                     className="text-emerald-700 hover:underline" data-testid={`wa-${o.id}`}>
                    {md.beneficiary_phone || md.client_phone}
                  </a>
                  <QuickCopy value={phone} label="Teléfono" testId={`copy-phone-${o.id}`} />
                </div>
              )}
              {senderName && (
                <p className="text-[10px] text-slate-500 mt-2 pt-2 border-t">
                  De: <b className="text-slate-700">{senderName}</b>
                  {o.created_by_info?.role && <span className="ml-1">({o.created_by_info.role})</span>}
                </p>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  </Card>
);

export default HomeLandingPage;
